import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import Meta from '../../components/layout/Meta';
import PageLayout from '../../layouts/PageLayout';
import PageTitleWithActions from '../../layouts/PageLayout/TitleWithActions';
import Button from '../../components/shared/Button';
import ForumMembers from '../../modules/forumMembers/UI';

import { FORUM_MEMBER_ADD_PATH } from '../../constants';
import { usePermission } from '../../hooks/usePermission';
import { useRedirect } from '../../hooks/useRedirect';
import { PermissionsEnum } from '../../types';
import {
  ForumMembersFiltersProps,
  ForumMembersReportFormDataInterface,
} from '../../modules/forumMembers/services/forumMembers.types';

import ForumMembersStore from '../../modules/forumMembers/services/forumMembers.store';
import { showToast } from '../../utils';
import { useRegions } from '../../context/regions-context';
const forumMembersStore: ForumMembersStore = new ForumMembersStore();

function ForumMembersPage() {
  const { t } = useTranslation();

  const { activeRegion } = useRegions();

  const pageTitle = t('forum_members.title_forum_members');
  const canListUsers = usePermission([PermissionsEnum.UsersList]);
  const canAddMember = usePermission([PermissionsEnum.UserCreate]);
  const canGenerateReport = usePermission([PermissionsEnum.UserReport]);
  const shouldRedirect = canListUsers === false && canAddMember === false;

  useRedirect({ redirect: shouldRedirect });

  // State to store selected filters
  const [selectedFilters, setSelectedFilters] = useState<
    ForumMembersFiltersProps | undefined
  >(undefined);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  // Callback function to receive selected filters from ForumMembers
  const handleSelectedFilters = useCallback(
    (data: ForumMembersFiltersProps) => {
      setSelectedFilters(data);
    },
    []
  );

  const handleFileDownload = async (data: Blob) => {
    if (!canGenerateReport) return;

    const fileUrl = window.URL.createObjectURL(data);

    const link = document.createElement('a');
    link.href = fileUrl;

    const regionName = activeRegion?.name;
    const organizationName = selectedFilters?.organization?.name;
    const searchTerm = selectedFilters?.searchTerm;

    let statusName = '';
    switch (selectedFilters?.status.id) {
      case 0:
        statusName = t('status.deactivated_members');
        break;
      case 1:
        statusName = t('status.active_members');
        break;
      case 2:
        statusName = t('status.pending_members');
        break;
    }

    // Create the filename dynamically based on available filters
    let filename = `${t('forum_members.forum_members_report')}`;

    if (regionName) filename += `_${regionName}`;
    if (statusName) filename += `_${statusName}`;
    if (organizationName) filename += `_${organizationName}`;
    if (searchTerm) filename += `_${searchTerm}`;
    // Set the desired filename for the download ADD NAME
    link.download = filename;

    // Trigger the click event to start the download automatically
    link.click();

    // Revoke the Blob URL after download to free up memory
    URL.revokeObjectURL(fileUrl);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canGenerateReport) return;

    setIsGeneratingReport(true);

    if (!canListUsers || !selectedFilters) return;

    const payload: ForumMembersReportFormDataInterface = {
      status: selectedFilters.status.id,
      searchTerm: selectedFilters.searchTerm,
      organization: selectedFilters?.organization?.id,
    };

    const response = await forumMembersStore.getForumMembersReport({
      payload,
    });

    const { success, message } = response;

    if (success) {
      const responseData = response.data;
      if (!responseData) return;
      handleFileDownload(responseData);
    }

    showToast(message, success);

    setIsGeneratingReport(false);
  };

  return (
    <>
      <Meta title={pageTitle} />
      <PageLayout>
        <PageTitleWithActions title={pageTitle}>
          <div className="w-full flex flex-wrap gap-4">
            {canAddMember && (
              <Link
                to={FORUM_MEMBER_ADD_PATH}
                className="focus:outline-none group flex-grow"
              >
                <Button tabIndex={-1} className="w-full">
                  {t('forum_members.link_forum_member_add')}
                </Button>
              </Link>
            )}
            {canGenerateReport && (
              <form onSubmit={handleSubmit} className="flex-grow">
                <Button
                  className="w-full"
                  disabled={isGeneratingReport}
                  isDisabled={isGeneratingReport}
                >
                  {t('fraud_cases.generate_report')}
                </Button>
              </form>
            )}
          </div>
        </PageTitleWithActions>

        {canListUsers && (
          <ForumMembers
            onExtractFilters={canListUsers ? handleSelectedFilters : undefined}
          />
        )}
      </PageLayout>
    </>
  );
}

export default ForumMembersPage;
